import React, { useState, useEffect } from 'react';
import { FiSearch, FiFilter, FiUser, FiEdit2, FiTrash2, FiPlus, FiEye } from 'react-icons/fi';
import { getActivityLog } from '../services/activityLog';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Pagination from '../components/Common/Pagination';
import toast from 'react-hot-toast';

const actionIcons = {
  create: <FiPlus className="text-green-500" />,
  update: <FiEdit2 className="text-blue-500" />,
  delete: <FiTrash2 className="text-red-500" />,
  view: <FiEye className="text-gray-500" />,
  login: <FiUser className="text-purple-500" />,
  assign: <FiUser className="text-orange-500" />
};

const actionColors = {
  create: 'bg-green-100 text-green-800',
  update: 'bg-blue-100 text-blue-800',
  delete: 'bg-red-100 text-red-800',
  view: 'bg-gray-100 text-gray-800',
  login: 'bg-purple-100 text-purple-800',
  assign: 'bg-orange-100 text-orange-800'
};

export default function ActivityLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ action: '', resource: '', user: '', page: 1, limit: 50 });
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchLogs();
  }, [filters]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const data = await getActivityLog(filters);
      setLogs(data.logs || []);
      setTotalPages(data.pagination?.pages || 1);
    } catch (error) {
      toast.error('Failed to fetch activity log');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Activity Log</h1>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Action</label>
            <select
              value={filters.action}
              onChange={(e) => setFilters({ ...filters, action: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All</option>
              <option value="create">Create</option>
              <option value="update">Update</option>
              <option value="delete">Delete</option>
              <option value="view">View</option>
              <option value="login">Login</option>
              <option value="assign">Assign</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm text-gray-600 mb-1">Resource</label>
            <select
              value={filters.resource}
              onChange={(e) => setFilters({ ...filters, resource: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All</option>
              <option value="order">Order</option>
              <option value="driver">Driver</option>
              <option value="tour_guide">Tour Guide</option>
              <option value="tour_company">Tour Company</option>
              <option value="service">Service</option>
              <option value="blog">Blog</option>
              <option value="affiliate">Affiliate</option>
              <option value="user">User</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm text-gray-600 mb-1">User</label>
            <input
              type="text"
              placeholder="Username"
              value={filters.user}
              onChange={(e) => setFilters({ ...filters, user: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          
          <div className="flex items-end">
            <button
              onClick={() => setFilters({ ...filters, page: 1 })}
              className="w-full px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Logs Table */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Resource</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {logs.map((log) => (
                  <tr key={log._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm">{formatDate(log.created_at)}</td>
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-800">{log.user_name}</p>
                      <p className="text-xs text-gray-500">{log.user_role}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${actionColors[log.action]}`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm capitalize">{log.resource}</td>
                    <td className="px-6 py-4 text-sm">
                      {log.resource_name || log.resource_id}
                      {log.details?.changes && (
                        <span className="text-xs text-gray-400 ml-1">
                          ({log.details.changes.length} changes)
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">{log.user_ip || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {logs.length === 0 && (
            <div className="text-center py-12 text-gray-500">No activity logs found</div>
          )}
          
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t">
              <Pagination
                currentPage={filters.page}
                totalPages={totalPages}
                onPageChange={(page) => setFilters({ ...filters, page })}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
